import { useState } from "react";
import { Bell, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { TicketDialog } from "@/components/bug-reports/ticket-dialog";
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
  type AppNotification,
} from "@/hooks/use-notifications";
import { relativeTime } from "@/lib/relative-time";
import { cn, formatDateTime } from "@/lib/utils";

function NotificationItem({
  notification: n,
  onOpen,
  onMarkRead,
}: {
  notification: AppNotification;
  onOpen: () => void;
  onMarkRead: () => void;
}) {
  const unread = n.readAt === null;
  const date = new Date(n.createdAt * 1000);

  return (
    <li className="relative border-b last:border-b-0 hover:bg-accent/60">
      {/* Cijela stavka je klikabilna; „Označi pročitano“ leži iznad nje (z-10). */}
      <button
        type="button"
        onClick={onOpen}
        aria-label={n.title}
        className="absolute inset-0 outline-none focus-visible:ring-[3px] focus-visible:ring-inset focus-visible:ring-ring/50"
      />
      <div className="flex gap-2.5 px-3 py-2.5">
        <span
          aria-hidden
          className={cn("mt-1.5 size-1.5 shrink-0 rounded-full", unread ? "bg-primary" : "bg-transparent")}
        />
        <div className="min-w-0 flex-1 space-y-0.5">
          <p className={cn("break-words text-[13px] leading-snug", unread ? "font-medium" : "text-muted-foreground")}>
            {n.title}
          </p>
          {n.body && (
            <p className="line-clamp-2 break-words text-xs text-muted-foreground">{n.body}</p>
          )}
          <div className="flex items-center justify-between gap-2 pt-0.5">
            <time
              dateTime={date.toISOString()}
              title={formatDateTime(date)}
              className="text-[11px] tabular-nums text-muted-foreground"
            >
              {relativeTime(n.createdAt)}
            </time>
            {unread && (
              <button
                type="button"
                onClick={onMarkRead}
                className="relative z-10 text-[11px] text-muted-foreground underline-offset-2 outline-none hover:text-foreground hover:underline focus-visible:ring-[3px] focus-visible:ring-ring/50"
              >
                Označi pročitano
              </button>
            )}
          </div>
        </div>
      </div>
    </li>
  );
}

/**
 * Zvonce s obavijestima. Montira se i u sidebaru i u mobilnom zaglavlju; oba dijele
 * isti react-query keš, pa se lista dohvaća jednom bez obzira na broj instanci.
 */
export function NotificationBell({ align = "start" }: { align?: "start" | "end" }) {
  const [open, setOpen] = useState(false);
  const [ticketId, setTicketId] = useState<string | null>(null);
  const { data } = useNotifications();
  const markRead = useMarkNotificationRead();
  const markAll = useMarkAllNotificationsRead();

  const unread = data?.unread ?? 0;
  const items = data?.items ?? [];

  function openItem(n: AppNotification) {
    if (n.readAt === null) markRead.mutate(n.id);
    if (n.linkKind === "bug_report" && n.linkId) {
      setOpen(false);
      setTicketId(n.linkId);
    }
  }

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="icon-xs"
            className="relative"
            aria-label={unread > 0 ? `Obavijesti, nepročitanih: ${unread}` : "Obavijesti"}
            title="Obavijesti"
          >
            <Bell className="h-4 w-4" />
            {unread > 0 && (
              <span className="absolute -right-1 -top-1 flex h-[15px] min-w-[15px] items-center justify-center rounded-full bg-primary px-0.5 text-[9px] font-medium leading-none text-primary-foreground">
                {unread > 9 ? "9+" : unread}
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent align={align} className="w-80 p-0">
          <div className="flex items-center justify-between gap-2 border-b px-3 py-2">
            <p className="text-xs font-medium">Obavijesti</p>
            {unread > 0 && (
              <button
                type="button"
                onClick={() => markAll.mutate()}
                className="inline-flex items-center gap-1 text-[11px] text-muted-foreground outline-none hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50"
              >
                <Check className="size-3" />
                Označi sve pročitano
              </button>
            )}
          </div>
          {items.length === 0 ? (
            <p className="px-3 py-8 text-center text-xs text-muted-foreground">Nema obavijesti</p>
          ) : (
            <ul className="max-h-[min(28rem,70vh)] overflow-y-auto">
              {items.map((n) => (
                <NotificationItem
                  key={n.id}
                  notification={n}
                  onOpen={() => openItem(n)}
                  onMarkRead={() => markRead.mutate(n.id)}
                />
              ))}
            </ul>
          )}
        </PopoverContent>
      </Popover>

      <TicketDialog reportId={ticketId} onClose={() => setTicketId(null)} />
    </>
  );
}
